import { useSortable } from '@dnd-kit/sortable';
import { Box, Typography, useTheme } from '@mui/material';
import React from 'react';
import { PositionLeg } from 'types/optionsCalculator';
import SortableSelectedStrike from './SortableSelectedStrike';

// Combined strike slot with integrated selected strikes
const StrikeSlot = React.memo(
  ({
    strike,
    position,
    isOver,
    legs,
  }: {
    strike: number;
    position: number;
    isOver: boolean;
    legs: PositionLeg[];
  }) => {
    const theme = useTheme();

    const { setNodeRef } = useSortable({
      id: `slot-${strike}`,
      data: { type: 'slot', strike },
    });

    // Separate buy and sell legs
    const buyLegs = legs.filter((leg) => leg.quantity > 0);
    const sellLegs = legs.filter((leg) => leg.quantity < 0);

    return (
      <Box
        ref={setNodeRef}
        sx={{
          position: 'absolute',
          left: position,
          top: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        {/* Buy legs above the strike */}
        {buyLegs.map((leg) => (
          <SortableSelectedStrike
            key={leg.id}
            strike={strike}
            position={0}
            leg={leg}
            placement="above"
          />
        ))}

        {/* Strike slot */}
        <Box
          sx={{
            width: '2px',
            height: 8,
            backgroundColor: isOver
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
            opacity: isOver ? 1 : 0.8,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.65rem',
            color: isOver
              ? theme.palette.getContrastText(theme.palette.primary.main)
              : theme.palette.text.primary,
            fontWeight: isOver ? 'bold' : 'medium',
            whiteSpace: 'nowrap',
            margin: '2px 0',
            backgroundColor: isOver
              ? theme.palette.primary.light
              : 'transparent',
            padding: isOver ? '2px 4px' : '0',
            borderRadius: isOver ? '4px' : '0',
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {strike}
        </Typography>
        <Box
          sx={{
            width: '2px',
            height: 8,
            backgroundColor: isOver
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
            opacity: isOver ? 1 : 0.8,
          }}
        />

        {/* Sell legs below the strike */}
        {sellLegs.map((leg) => (
          <SortableSelectedStrike
            key={leg.id}
            strike={strike}
            position={0}
            leg={leg}
            placement="below"
          />
        ))}
      </Box>
    );
  },
);

export default StrikeSlot;
